import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
} from 'date-fns';
import api from '../services/api';
import { formatDateTimeFR } from '../utils/dateUtils';
import AppointmentCalendar from '../components/appointments/AppointmentCalendar';
import AppointmentDayPanel from '../components/appointments/AppointmentDayPanel';

const STATUS_BADGES = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const ParentAppointmentsManagePage = () => {
  const [view, setView] = useState('calendar');
  const [appointments, setAppointments] = useState([]);
  const [calendarData, setCalendarData] = useState({ days: [], events: [], config: null });
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const loadList = useCallback(() => {
    setLoading(true);
    api.get('/parent-portal/appointments-manage', { params: filter ? { status: filter } : {} })
      .then((res) => { if (res.data.success) setAppointments(res.data.data); })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [filter]);

  const loadCalendar = useCallback(() => {
    setCalendarLoading(true);
    const from = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
    const to = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

    api.get('/parent-portal/appointments-manage/calendar', { params: { from, to } })
      .then((res) => {
        if (res.data.success) {
          setCalendarData({
            days: res.data.days || [],
            events: res.data.events || [],
            config: res.data.config || null,
          });
        }
      })
      .catch(() => toast.error('Erreur de chargement du calendrier'))
      .finally(() => setCalendarLoading(false));
  }, [currentMonth]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const refreshAll = () => {
    loadList();
    loadCalendar();
  };

  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const selectedDayMeta = useMemo(
    () => calendarData.days.find((d) => d.date === selectedDateStr) || null,
    [calendarData.days, selectedDateStr]
  );
  const selectedDayEvents = useMemo(
    () => calendarData.events.filter((e) => e.date === selectedDateStr),
    [calendarData.events, selectedDateStr]
  );

  const handleConfirm = async (id) => {
    const result = await Swal.fire({
      title: 'Confirmer ce créneau ?',
      text: 'Le créneau proposé par le parent sera confirmé.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmer',
    });
    if (!result.isConfirmed) return;

    try {
      const res = await api.post(`/parent-portal/appointments-manage/${id}/confirm`);
      if (res.data.success) {
        toast.success(res.data.message);
        setSelectedAppointment(null);
        refreshAll();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur — créneau peut-être déjà pris');
    }
  };

  const handleReschedule = async (id) => {
    const result = await Swal.fire({
      title: 'Proposer un autre créneau',
      html: `
        <input id="swal-date" type="date" class="swal2-input" placeholder="Date">
        <input id="swal-time" type="time" class="swal2-input" placeholder="Heure">
        <textarea id="swal-notes" class="swal2-textarea" placeholder="Note pour le parent (optionnel)"></textarea>
      `,
      showCancelButton: true,
      confirmButtonText: 'Proposer',
      preConfirm: () => ({
        confirmed_date: document.getElementById('swal-date').value,
        confirmed_time: document.getElementById('swal-time').value,
        staff_notes: document.getElementById('swal-notes').value,
      }),
    });
    if (!result.isConfirmed) return;

    try {
      const res = await api.post(`/parent-portal/appointments-manage/${id}/reschedule`, result.value);
      if (res.data.success) {
        toast.success(res.data.message);
        setSelectedAppointment(null);
        refreshAll();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
  };

  const handleCancel = async (id) => {
    const result = await Swal.fire({
      title: 'Annuler ce RDV',
      input: 'textarea',
      inputLabel: 'Motif (optionnel)',
      showCancelButton: true,
      confirmButtonText: 'Annuler le RDV',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;

    try {
      const res = await api.post(`/parent-portal/appointments-manage/${id}/cancel`, { staff_notes: result.value });
      if (res.data.success) {
        toast.success(res.data.message);
        setSelectedAppointment(null);
        refreshAll();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
  };

  const handleSelectDate = (date) => {
    setSelectedDate(date);
    setSelectedAppointment(null);
  };

  const handleSelectAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    if (appointment?.date) {
      setSelectedDate(parseISO(appointment.date));
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rendez-vous parents</h1>
          <p className="text-sm text-gray-500">
            Visualisez les créneaux disponibles et gérez les demandes de rendez-vous
          </p>
        </div>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setView('calendar')}
            className={`px-4 py-2 text-sm font-medium ${
              view === 'calendar' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Calendrier
          </button>
          <button
            type="button"
            onClick={() => setView('list')}
            className={`px-4 py-2 text-sm font-medium ${
              view === 'list' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Liste
          </button>
        </div>
      </div>

      {view === 'calendar' ? (
        <div className="space-y-4">
          {calendarData.config && (
            <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              Créneaux : lundi–vendredi {calendarData.config.day_start}–{calendarData.config.day_end},
              {' '}samedi {calendarData.config.day_start}–{calendarData.config.saturday_end || '13:00'}
              {' '}(par tranches de {calendarData.config.slot_minutes} min).
            </div>
          )}

          {calendarLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="grid xl:grid-cols-2 gap-4">
              <AppointmentCalendar
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                days={calendarData.days}
                events={calendarData.events}
                selectedDate={selectedDate}
                onSelectDate={handleSelectDate}
              />
              <AppointmentDayPanel
                selectedDate={selectedDate}
                dayMeta={selectedDayMeta}
                events={selectedDayEvents}
                selectedAppointment={selectedAppointment}
                onSelectAppointment={handleSelectAppointment}
                onConfirm={handleConfirm}
                onReschedule={handleReschedule}
                onCancel={handleCancel}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-4">
            {['pending', 'confirmed', 'cancelled', ''].map((s) => (
              <button
                key={s || 'all'}
                type="button"
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  filter === s ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s === 'pending' ? 'En attente' : s === 'confirmed' ? 'Confirmés' : s === 'cancelled' ? 'Annulés' : 'Tous'}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full" />
            </div>
          ) : appointments.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center text-gray-500">Aucun rendez-vous</div>
          ) : (
            <div className="space-y-4">
              {appointments.map((a) => (
                <div key={a.id} className="bg-white rounded-lg border border-gray-200 p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{a.subject}</h3>
                      <p className="text-sm text-gray-500">
                        {a.parent_prenom} {a.parent_nom} — {a.parent_email || a.telmob1}
                      </p>
                      {a.message && <p className="text-sm text-gray-600 mt-1">{a.message}</p>}
                      <p className="text-sm mt-2">
                        <strong>Proposé :</strong> {formatDateTimeFR(a.proposed_date, a.proposed_time)}
                      </p>
                      {a.confirmed_date && (
                        <p className="text-sm text-green-700">
                          <strong>Confirmé :</strong> {formatDateTimeFR(a.confirmed_date, a.confirmed_time)}
                        </p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_BADGES[a.status]}`}>
                      {a.status === 'pending' ? 'En attente' : a.status === 'confirmed' ? 'Confirmé' : 'Annulé'}
                    </span>
                  </div>

                  {a.status === 'pending' && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleConfirm(a.id)}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                      >
                        Confirmer le créneau proposé
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReschedule(a.id)}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                      >
                        Proposer un autre créneau
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCancel(a.id)}
                        className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                      >
                        Annuler
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ParentAppointmentsManagePage;
